import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styles from "./styles.module.scss";
import Tick from "../../assets/svg/icons/common/tick";
import FileIcon from "../../assets/svg/icons/common/file";

const Select = ({
  options,
  value,
  onChange,
  placeholder = "Search...",
  isMultiSelect = false,
  onBlur,
}) => {
  const [search, setSearch] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownStyles, setDropdownStyles] = useState({});
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const selectedValues = isMultiSelect
    ? Array.isArray(value)
      ? value
      : []
    : options.find((option) => option.value === value) || null;

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = (option) => {
    if (isMultiSelect) {
      const isSelected = selectedValues.some(
        (item) => item.value === option.value,
      );
      const updatedValues = isSelected
        ? selectedValues.filter((item) => item.value !== option.value)
        : [...selectedValues, option];

      onChange(updatedValues);
    } else {
      onChange(option.value);
    }

    setIsDropdownOpen(false);
    setSearch("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(isMultiSelect ? [] : "");
    setSearch("");
  };

  const updateDropdownPosition = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownStyles({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  };

  useEffect(() => {
    if (isDropdownOpen) {
      updateDropdownPosition();
      window.addEventListener("scroll", updateDropdownPosition);
      window.addEventListener("resize", updateDropdownPosition);
    }

    return () => {
      window.removeEventListener("scroll", updateDropdownPosition);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [isDropdownOpen]);

  return (
    <div className={styles.selectContainer} ref={inputRef}>
      <div
        className={styles.inputWrapper}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        {isMultiSelect || !selectedValues ? (
          <input
            type="text"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.input}
          />
        ) : (
          <div className={styles.selectedSingleValue}>
            {selectedValues.label}
          </div>
        )}
        {(selectedValues || search) && (
          <button className={styles.clearButton} onClick={handleClear}>
            ✕
          </button>
        )}
      </div>

      {isDropdownOpen &&
        createPortal(
          <ul
            className={styles.dropdown}
            ref={dropdownRef}
            style={dropdownStyles}
          >
            {filteredOptions.length === 0 ? (
              <li className={styles.noOptions}>No options found</li>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = isMultiSelect
                  ? selectedValues.some((item) => item.value === option.value)
                  : selectedValues?.value === option.value;

                return (
                  <li
                    key={option.value}
                    className={`${styles.option} ${isSelected ? styles.selectedOption : ""}`}
                    onClick={() => handleSelect(option)}
                  >
                    {isSelected && (
                      <span className={styles.checkmark}>
                        <Tick />
                      </span>
                    )}
                    {option.file ? (
                      <span className={styles.fileOption}>
                        <FileIcon /> {option.label}{" "}
                        <a
                          href={option.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          (Open)
                        </a>
                      </span>
                    ) : (
                      option.label
                    )}
                  </li>
                );
              })
            )}
          </ul>,
          document.body,
        )}

      {isMultiSelect && selectedValues.length > 0 && (
        <div className={styles.selectedOptions}>
          {selectedValues.map((option) => (
            <div key={option.value} className={styles.selectedItem}>
              {option.file ? (
                <span className={styles.fileOption}>
                  <a
                    href={option.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FileIcon /> {option.label}{" "}
                  </a>
                </span>
              ) : (
                option.label
              )}
              <button
                className={styles.deleteButton}
                onClick={() => handleSelect(option)}
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Select;
